import React, { useEffect, useState } from "react"
import { useTrack, useVariation } from "@hackler/react-sdk"
import usePlatform from "../../../hooks/usePlatform"
import useScrollImpressionLog from "../../../hooks/useScrollImpressionLog"
import { Box, Button, Grid, Typography } from "@material-ui/core"
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles"
import DemoRequestFormDialog from "../../../components/Dialog/DemoRequestFormDialog"
import { FormattedMessage } from "react-intl"
import { enFontFamily } from "../../../layouts/MainFrame"
import useTour from "../../../hooks/useTour"
import ProgressButton from "../../../components/Button/ProgressButton"
import homepageAtf1 from "../../../assets/images/home/homepage_atf_1_en.png"
import homepageAtf2 from "../../../assets/images/home/homepage_atf_2_en.png"
import homepageAtf3 from "../../../assets/images/home/homepage_atf_3_en.png"
import homepageAtf4 from "../../../assets/images/home/homepage_atf_4_en.png"
import homepageAtf1Mo from "../../../assets/images/home/homepage_atf_1_mo_en.png"
import homepageAtf2Mo from "../../../assets/images/home/homepage_atf_2_mo_en.png"
import homepageAtf3Mo from "../../../assets/images/home/homepage_atf_3_mo_en.png"
import homepageAtf4Mo from "../../../assets/images/home/homepage_atf_4_mo_en.png"

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      background: "linear-gradient(284.04deg, #517FFF 0%, #234AB6 100%)",
    },
    containerBox: {
      position: "relative",
      margin: "0 auto",
      maxWidth: "1440px",
    },
    container: {
      maxWidth: "1200px",
      margin: "0 auto",
      flexWrap: "nowrap",
      [theme.breakpoints.up("md")]: {
        padding: "88px 10px",
      },
    },
    textWrapper: {
      padding: "41px 20px 0",
      [theme.breakpoints.up("md")]: {
        padding: "50px 0 60px 0",
      },
    },
    title: {
      color: theme.palette.common.white,
      textAlign: "center",
      fontSize: 24,
      letterSpacing: -1,
      marginBottom: 14,
      [theme.breakpoints.up("md")]: {
        textAlign: "left",
        fontSize: 40,
        width: 660,
      },
    },
    allInONe: {
      color: "#fff",
      opacity: 0.8,
      fontSize: 14,
      fontWeight: 700,
      fontFamily: enFontFamily,
      marginBottom: 8,
      [theme.breakpoints.up("md")]: {
        fontSize: 24,
        marginBottom: 0,
      },
    },
    engTitle: {
      lineHeight: 1.3,
      "& p": {
        margin: 0,
      },
      [theme.breakpoints.up("md")]: {
        lineHeight: 1.4,
      },
    },
    hackle: {
      marginTop: 8,
      [theme.breakpoints.up("md")]: {
        marginTop: 40,
      },
    },
    buttonWrapper: {
      textAlign: "center",
      "& button": {
        textTransform: "none",
      },
      [theme.breakpoints.up("md")]: {
        textAlign: "inherit",
      },
    },
    button: {
      borderRadius: 8,
      backgroundColor: theme.palette.common.white,
      color: theme.palette.primary.main,
      fontSize: 14,
      marginRight: 4,
      padding: "8px 32px",
      "&:hover": {
        backgroundColor: theme.palette.common.white,
        color: theme.palette.primary.main,
        opacity: 0.8,
      },
      [theme.breakpoints.up("md")]: {
        fontSize: 20,
        marginRight: 4,
        padding: "12px 32px",
      },
    },
    buttonSmall: {
      color: theme.palette.common.white,
      padding: 4,
      fontSize: 14,
      margin: "-4px 4px 0 0",
      [theme.breakpoints.up("md")]: {
        padding: "12px 16px 12px 0",
        fontSize: 20,
        marginRight: 4,
      },
    },
    videoContainer: {
      position: "relative",
      marginTop: 40,
      paddingTop: "7%",
      // paddingLeft: 28,
      paddingRight: 28,
      [theme.breakpoints.up("md")]: {
        maxWidth: 620,
        paddingTop: "3.5%",
        paddingRight: "inherit",
      },
    },
    imageWrapper: {
      position: "relative",
      width: "100%",
      [theme.breakpoints.up("md")]: {
        width: "50%",
      },
    },
    videoWrapper: {
      borderRadius: 10,
      overflow: "hidden",
      "-webkit-backface-visibility": "hidden",
      "-moz-backface-visibility": "hidden",
      "-webkit-transform": "translate3d(0, 0, 0)",
      "-moz-transform": "translate3d(0, 0, 0)",
      marginRight: 12,
      [theme.breakpoints.up("md")]: {
        borderRadius: 24,
        marginRight: 58,
      },
    },
    videoOverlay: {
      width: "100%",
    },
    introduceVideo: {
      width: "100%",
      display: "block",
    },
    mainAbsoluteImage: {
      position: "absolute",
      top: 0,
      left: "-33px",
      "& img": {
        height: "60%",
        [theme.breakpoints.up("md")]: {
          width: 754,
        },
      },
    },
    mainImage: {
      position: "absolute",
      left: "-80px",
      top: "50px",
      "& img": {
        height: "50%",
        [theme.breakpoints.up("md")]: {
          width: 660,
        },
      },
    },
    mobileImage: {
      marginTop: "30px",
      padding: "0 10px",
      "& img": {
        width: "100%",
      },
    },
  })
)

export default function HeroSectionEn() {
  const track = useTrack()
  const { isMobile } = usePlatform()
  const { ref } = useScrollImpressionLog({ message: "1: 배너", eventKey: "homepage_home_section_1_impression" })
  const tour = useTour()
  const [submitting, setSubmitting] = useState(false)
  const classes = useStyles()
  const [index, setIndex] = useState(0)
  const images = [homepageAtf1, homepageAtf2, homepageAtf3, homepageAtf4]
  const mobileImages = [homepageAtf1Mo, homepageAtf2Mo, homepageAtf3Mo, homepageAtf4Mo]

  const getImageSrc = () => {
    return isMobile ? mobileImages[index] : images[index]
  }

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prevIndex) => (prevIndex === 3 ? 0 : prevIndex + 1))
    }, 1500)
    return () => {
      clearInterval(timer)
    }
  }, [])

  return (
    <div ref={ref} className={classes.root}>
      <Box className={classes.containerBox}>
        <Grid container justify={"space-between"} className={classes.container} direction={isMobile ? "column" : "row"}>
          <Grid item className={classes.textWrapper}>
            <Grid container direction={"column"}>
              <Grid item>
                <Typography className={classes.title}>
                  <Typography className={classes.allInONe}>All-in-One Platform</Typography>
                  <strong className={classes.engTitle}>
                    <p>Don’t juggle {isMobile && <br />}between platforms</p>
                    <p>to optimize your product.</p>
                    <div className={classes.hackle}>Use Hackle.</div>
                  </strong>
                </Typography>
              </Grid>
              <Grid item>
                <Box mt={isMobile ? 2 : 6}>
                  <Grid
                    container
                    className={classes.buttonWrapper}
                    spacing={isMobile ? 1 : 4}
                    direction={isMobile ? "column" : "row"}>
                    <Grid item>
                      <ProgressButton
                        progress={submitting}
                        onClick={() => {
                          setSubmitting(true)
                          track({
                            key: "homepage_tour_click",
                            properties: {
                              area: "section_1",
                            },
                          })

                          try {
                            //@ts-ignore
                            analytics.track("homepage_tour_click", { area: "hero_section" })
                          } catch (error) {}

                          tour()
                        }}
                        color={"primary"}
                        variant={"contained"}
                        className={classes.button}>
                        <strong>
                          <FormattedMessage id={"cta_tour"} />
                        </strong>
                      </ProgressButton>
                    </Grid>
                    <Grid item>
                      <DemoRequestFormDialog
                        ButtonElement={(handleButtonClick) => (
                          <Button
                            variant={"text"}
                            className={classes.buttonSmall}
                            onClick={() => {
                              track({ key: "homepage_home_section_1_request_demo_click" })

                              try {
                                //@ts-ignore
                                analytics.track("homepage_request_demo_click", { area: "hero_section" })
                              } catch (error) {}

                              handleButtonClick()
                            }}>
                            <strong>
                              <FormattedMessage id={"REQUEST_DEMO_TITLE"} />
                            </strong>
                          </Button>
                        )}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid item className={classes.imageWrapper}>
            {isMobile ? (
              <Grid className={classes.mobileImage}>
                <img src={getImageSrc()} alt={"All-in-One Platform Hackle"} />
              </Grid>
            ) : (
              <Grid className={classes.mainImage}>
                <img src={getImageSrc()} alt={"All-in-One Platform Hackle"} />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Box>
    </div>
  )
}
